/**
 * Shared image-upload rules that both the browser and the backend can read.
 *
 * The Studio's cover uploader now has a different size ceiling from the app's other
 * image surfaces, which means "the image rules" are no longer one hard-coded number.
 * The browser still needs File validation, the server still needs data-URL validation,
 * and both need to speak with the same type list and the same user-facing labels.
 */

export interface ImageUploadPolicy {
  /** MIME types this upload surface accepts. */
  acceptedTypes: readonly string[];
  /** The `accept` attribute built from those MIME types. */
  acceptAttribute: string;
  /** Human list for helper text and validation messages. */
  acceptedLabel: string;
  /** Largest raw file accepted, in bytes. */
  maxBytes: number;
  /** `maxBytes` as a human string for helper text and validation messages. */
  maxLabel: string;
}

export type ImageRejection =
  | { reason: 'type'; message: string }
  | { reason: 'size'; message: string }
  | { reason: 'empty'; message: string };

/** Formats the picker accepts, as MIME types. */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;

/** The `accept` attribute for the file input, derived so the two cannot disagree. */
export const ACCEPT_ATTRIBUTE = ACCEPTED_IMAGE_TYPES.join(',');

/** Human list for the upload area's helper text. */
export const ACCEPTED_IMAGE_LABEL = 'PNG, JPG, WEBP or GIF';

/** The default image-upload ceiling used outside the Studio cover uploader. */
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

/** MAX_IMAGE_BYTES as a human string, for messages. */
export const MAX_IMAGE_LABEL = '8MB';

/** The Studio song-cover ceiling. */
export const STUDIO_COVER_MAX_IMAGE_BYTES = 14 * 1024 * 1024;

/** STUDIO_COVER_MAX_IMAGE_BYTES as a human string. */
export const STUDIO_COVER_MAX_IMAGE_LABEL = '14MB';

export const DEFAULT_IMAGE_UPLOAD_POLICY: ImageUploadPolicy = {
  acceptedTypes: ACCEPTED_IMAGE_TYPES,
  acceptAttribute: ACCEPT_ATTRIBUTE,
  acceptedLabel: ACCEPTED_IMAGE_LABEL,
  maxBytes: MAX_IMAGE_BYTES,
  maxLabel: MAX_IMAGE_LABEL,
};

export const STUDIO_COVER_IMAGE_POLICY: ImageUploadPolicy = {
  acceptedTypes: ACCEPTED_IMAGE_TYPES,
  acceptAttribute: ACCEPT_ATTRIBUTE,
  acceptedLabel: ACCEPTED_IMAGE_LABEL,
  maxBytes: STUDIO_COVER_MAX_IMAGE_BYTES,
  maxLabel: STUDIO_COVER_MAX_IMAGE_LABEL,
};

/** Bytes as a short human string, e.g. "12.4MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

/** File-like validation shared by browser file picks and server-side data URLs. */
export function validateImageFileLike(
  file: { type: string; size: number },
  policy: ImageUploadPolicy = DEFAULT_IMAGE_UPLOAD_POLICY
): ImageRejection | null {
  if (file.size === 0) {
    return { reason: 'empty', message: 'That file is empty. Pick an image file.' };
  }
  if (!(policy.acceptedTypes as readonly string[]).includes(file.type)) {
    return {
      reason: 'type',
      message: `That file isn't a supported image. Use ${policy.acceptedLabel}.`,
    };
  }
  if (file.size > policy.maxBytes) {
    return {
      reason: 'size',
      message: `That image is ${formatBytes(file.size)}. The limit is ${policy.maxLabel}.`,
    };
  }
  return null;
}

export interface ParsedImageDataUrl {
  mimeType: string;
  byteLength: number;
}

/**
 * Reads the MIME type and decoded size out of a base64 image data URL.
 *
 * The Studio persists uploaded covers as data URLs today, so the backend's equivalent of
 * a file-size check has to read the base64 envelope rather than a multipart stream.
 */
export function parseImageDataUrl(dataUrl: string): ParsedImageDataUrl | null {
  const match = /^data:([^;,]+);base64,([A-Za-z0-9+/]*={0,2})$/.exec(dataUrl);
  if (!match) return null;
  const mimeType = match[1] ?? '';
  const base64 = match[2] ?? '';
  const byteLength = base64ByteLength(base64);
  if (byteLength === null) return null;
  return { mimeType, byteLength };
}

/** Validates a base64 image data URL against the same policy a file pick uses. */
export function validateImageDataUrl(
  dataUrl: string,
  policy: ImageUploadPolicy = DEFAULT_IMAGE_UPLOAD_POLICY
): ImageRejection | null {
  const parsed = parseImageDataUrl(dataUrl);
  if (!parsed) {
    return {
      reason: 'type',
      message: `That file isn't a supported image. Use ${policy.acceptedLabel}.`,
    };
  }
  return validateImageFileLike({ type: parsed.mimeType, size: parsed.byteLength }, policy);
}

function base64ByteLength(base64: string): number | null {
  if (base64.length === 0 || base64.length % 4 !== 0) return null;
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) return null;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return (base64.length / 4) * 3 - padding;
}
